'use client'

import { useRouter } from 'next/navigation'
import { m } from 'framer-motion'
import { useEffect } from 'react'
import { projects, type TechItem } from '@/data/projects'
import Carousel from '@/components/ui/Carousel'

const statusLabel = { active: 'ACTIVE', archived: 'ARCHIVED', classified: 'CLASSIFIED', 'on-hold': 'ON HOLD', 'going-live': 'GOING LIVE' }
const statusColor = {
  active: 'text-green-400 border-green-400/30',
  archived: 'text-batman-muted border-batman-muted/30',
  classified: 'text-red-400 border-red-400/30',
  'on-hold': 'text-yellow-400 border-yellow-400/30',
  'going-live': 'text-sky-400 border-sky-400/30',
}

const categoryLabel = {
  frontend: 'Frontend',
  backend: 'Backend',
  tooling: 'Tooling',
  design: 'Design',
}

const categoryColor = {
  frontend: 'dark:border-batman-glow/40 border-bruce-blue/30 dark:text-batman-glow text-bruce-blue dark:bg-batman-blue/10 bg-bruce-blue/5',
  backend: 'dark:border-green-500/30 border-green-600/30 dark:text-green-400 text-green-600 dark:bg-green-500/5 bg-green-600/5',
  tooling: 'dark:border-batman-gold/40 border-bruce-gold/40 dark:text-batman-gold text-bruce-gold dark:bg-batman-gold/10 bg-bruce-gold/5',
  design: 'dark:border-purple-400/30 border-purple-600/30 dark:text-purple-400 text-purple-600 dark:bg-purple-500/5 bg-purple-600/5',
}

function groupByCategory(techStack: TechItem[]) {
  const groups: Record<string, TechItem[]> = {}
  techStack.forEach(item => {
    if (!groups[item.category]) groups[item.category] = []
    groups[item.category].push(item)
  })
  return groups
}

export default function ProjectDetailClient({ slug }: { slug: string }) {
  const router = useRouter()

  const project = projects.find(p => p.slug === slug)
  const currentIndex = projects.findIndex(p => p.slug === slug)
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null
  const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [slug])

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-cinzel text-2xl dark:text-batman-text text-bruce-navy">Case Not Found</p>
        <button
          onClick={() => router.push('/#case-files')}
          className="font-mono text-sm dark:text-batman-gold text-bruce-gold hover:underline"
        >
          ← Return to Case Files
        </button>
      </div>
    )
  }

  const techGroups = groupByCategory(project.techStack)

  return (
    // CSS fade-in so the page (and its LCP image) shows before animation JS loads
    <div className="min-h-screen pt-24 pb-20 page-fade-in">
      <div className="max-w-5xl mx-auto px-6">
        {/* Back button */}
        <m.button
          onClick={() => router.push('/#case-files')}
          className="flex items-center gap-2 font-mono text-xs tracking-widest uppercase dark:text-batman-muted text-bruce-muted hover:dark:text-batman-gold hover:text-bruce-gold transition-colors mb-10 group"
          whileHover={{ x: -4 }}
        >
          <span className="group-hover:-translate-x-1 transition-transform">←</span>
          Case Files
        </m.button>

        {/* Header */}
        <div className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className={`text-xs font-mono px-2 py-0.5 rounded border ${statusColor[project.status]}`}>
              {statusLabel[project.status]}
            </span>
            <span className="text-xs font-mono dark:text-batman-muted text-bruce-muted px-2 py-0.5 rounded dark:bg-batman-surface/60 bg-white/60 border dark:border-white/10 border-bruce-navy/10">
              {project.role}
            </span>
            <span className="text-xs font-mono dark:text-batman-muted text-bruce-muted">
              {project.duration}
              {project.teamSize ? ` · ${project.teamSize} people` : ''}
            </span>
          </div>

          <h1 className="font-cinzel font-bold text-3xl sm:text-5xl dark:text-batman-text text-bruce-navy gold-glow mb-4">
            {project.title}
          </h1>

          <div className="section-line" />
        </div>

        {/* Carousel */}
        <div className="mb-10 w-full">
          <Carousel items={project.media} />
        </div>

        {/* Main content */}
        <div className="grid lg:grid-cols-5 gap-10 mb-16">
          {/* Left: Overview */}
          <div className="lg:col-span-3 space-y-8">
            <div>
              <p className="font-mono text-xs tracking-[0.3em] uppercase dark:text-batman-muted text-bruce-muted mb-3">
                // Overview
              </p>
              <p className="dark:text-batman-muted text-bruce-muted leading-relaxed text-[15px]">
                {project.overview}
              </p>
            </div>
          </div>

          {/* Right: Tech Stack + Links */}
          <div className="lg:col-span-2 space-y-8">
            {/* Tech Stack */}
            <div>
              <p className="font-mono text-xs tracking-[0.3em] uppercase dark:text-batman-gold text-bruce-gold mb-4">
                // Tech Stack
              </p>
              <div className="space-y-4">
                {(Object.keys(techGroups) as Array<keyof typeof categoryLabel>).map((cat, gi) => (
                  <m.div
                    key={cat}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: gi * 0.1 + 0.3 }}
                  >
                    <p className="font-mono text-xs dark:text-batman-muted text-bruce-muted mb-2">
                      {categoryLabel[cat]}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {techGroups[cat].map((tech, ti) => (
                        <m.span
                          key={tech.name}
                          className={`text-xs font-mono px-2.5 py-1 rounded border ${categoryColor[tech.category]}`}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: gi * 0.1 + ti * 0.04 + 0.4 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          {tech.name}
                        </m.span>
                      ))}
                    </div>
                  </m.div>
                ))}
              </div>
            </div>

            {/* Links */}
            <div className="flex flex-col gap-3">
              {project.live && project.live !== '#' && (
                <a
                  href={project.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 font-cinzel text-xs tracking-widest uppercase py-2.5 rounded border dark:border-batman-gold border-bruce-gold dark:text-batman-gold text-bruce-gold hover:dark:bg-batman-gold/10 hover:bg-bruce-gold/10 transition-colors"
                >
                  ↗ Live Site
                </a>
              )}
              {project.github && project.github !== '#' && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 font-cinzel text-xs tracking-widest uppercase py-2.5 rounded border dark:border-white/10 border-bruce-navy/20 dark:text-batman-muted text-bruce-muted hover:dark:text-batman-text hover:text-bruce-navy transition-colors"
                >
                  ⌥ Source Code
                </a>
              )}
            </div>

            {/* Tags */}
            <div>
              <p className="font-mono text-xs tracking-[0.3em] uppercase dark:text-batman-muted text-bruce-muted mb-2">
                // Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {project.tags.map(tag => (
                  <span key={tag} className="text-xs font-mono px-2 py-0.5 rounded dark:bg-batman-surface bg-white/60 dark:text-batman-muted text-bruce-muted border dark:border-white/10 border-bruce-navy/10">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Responsibilities */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="section-line flex-1" />
            <h2 className="font-cinzel font-semibold text-lg dark:text-batman-text text-bruce-navy tracking-widest whitespace-nowrap">
              <span className="dark:text-batman-gold text-bruce-gold">✦</span> FIELD NOTES
            </h2>
            <div className="section-line flex-1" />
          </div>

          <div className="space-y-4">
            {project.responsibilities.map((item, i) => (
              <m.div
                key={i}
                className="flex gap-4 dark:bg-batman-surface/40 bg-white/60 rounded-lg p-4 border dark:border-white/5 border-bruce-navy/10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 + 0.2 }}
              >
                <span className="font-cinzel font-bold text-xl dark:text-batman-gold/40 text-bruce-gold/50 shrink-0 leading-tight mt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="dark:text-batman-muted text-bruce-muted leading-relaxed text-[15px]">{item}</p>
              </m.div>
            ))}
          </div>
        </div>

        {/* Prev / Next navigation */}
        <div className="section-line mb-10" />
        <div className="grid sm:grid-cols-2 gap-4">
          {prevProject ? (
            <m.button
              onClick={() => router.push(`/projects/${prevProject.slug}`)}
              className="flex items-center gap-3 p-4 rounded-lg dark:bg-batman-surface/40 bg-white/60 border dark:border-white/5 border-bruce-navy/10 text-left group hover:dark:border-batman-gold/30 hover:border-bruce-gold/30 transition-colors"
              whileHover={{ x: -4 }}
            >
              <span className="dark:text-batman-gold text-bruce-gold text-lg">←</span>
              <div>
                <p className="font-mono text-xs dark:text-batman-muted text-bruce-muted">Previous</p>
                <p className="font-cinzel font-semibold text-sm dark:text-batman-text text-bruce-navy group-hover:text-batman-gold dark:group-hover:text-batman-gold transition-colors">
                  {prevProject.title}
                </p>
              </div>
            </m.button>
          ) : <div />}

          {nextProject && (
            <m.button
              onClick={() => router.push(`/projects/${nextProject.slug}`)}
              className="flex items-center justify-end gap-3 p-4 rounded-lg dark:bg-batman-surface/40 bg-white/60 border dark:border-white/5 border-bruce-navy/10 text-right group hover:dark:border-batman-gold/30 hover:border-bruce-gold/30 transition-colors sm:ml-auto w-full"
              whileHover={{ x: 4 }}
            >
              <div>
                <p className="font-mono text-xs dark:text-batman-muted text-bruce-muted">Next</p>
                <p className="font-cinzel font-semibold text-sm dark:text-batman-text text-bruce-navy group-hover:text-batman-gold dark:group-hover:text-batman-gold transition-colors">
                  {nextProject.title}
                </p>
              </div>
              <span className="dark:text-batman-gold text-bruce-gold text-lg">→</span>
            </m.button>
          )}
        </div>
      </div>
    </div>
  )
}
